/**
 * SVG Exporter
 * Serializes canvas layers to SVG format
 */

import type { Layer, TextLayer, ShapeLayer, ImageLayer, PrimitiveLayer } from '@/types/layers';
import type { AdFormat } from '@/types/formats';
import type { CanvasBackground } from '@/types/template';
import type { Brush, BackgroundBrush, GradientStop } from '@/types/brush';
import { resolvePosition, type ContainerDimensions } from '@/lib/position/resolver';

// Convert Brush to SVG fill/stroke value and optionally add defs
function brushToSvg(
  brush: Brush | undefined,
  ctx: ExportContext,
  prefix: string = 'brush'
): { value: string; def?: string } {
  if (!brush || brush.type === 'transparent') {
    return { value: 'none' };
  }

  if (brush.type === 'solid') {
    return { value: brush.color };
  }

  if (brush.type === 'linear-gradient') {
    const gradId = `${prefix}${ctx.defIdCounter++}`;
    const angle = brush.angle ?? 180;
    const rad = (angle - 90) * Math.PI / 180;
    const x1 = 50 - Math.cos(rad) * 50;
    const y1 = 50 - Math.sin(rad) * 50;
    const x2 = 50 + Math.cos(rad) * 50;
    const y2 = 50 + Math.sin(rad) * 50;
    const stops = brush.stops
      .map((s: GradientStop) => `<stop offset="${s.position}%" stop-color="${s.color}"/>`)
      .join('\n');
    const def = `<linearGradient id="${gradId}" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">\n${stops}\n</linearGradient>`;
    return { value: `url(#${gradId})`, def };
  }

  if (brush.type === 'radial-gradient') {
    const gradId = `${prefix}${ctx.defIdCounter++}`;
    const cx = brush.centerX ?? 50;
    const cy = brush.centerY ?? 50;
    const stops = brush.stops
      .map((s: GradientStop) => `<stop offset="${s.position}%" stop-color="${s.color}"/>`)
      .join('\n');
    const def = `<radialGradient id="${gradId}" cx="${cx}%" cy="${cy}%" r="50%">\n${stops}\n</radialGradient>`;
    return { value: `url(#${gradId})`, def };
  }

  // SVG doesn't natively support conic/sweep gradients - fall back to first color
  if (brush.type === 'sweep-gradient') {
    return { value: brush.stops[0]?.color ?? 'none' };
  }

  return { value: 'none' };
}

interface ExportContext {
  format: AdFormat;
  variableValues: Record<string, string>;
  defs: string[];
  defIdCounter: number;
}

/**
 * Export layers to SVG string
 */
export function exportToSVG(
  layers: Layer[],
  format: AdFormat,
  background: CanvasBackground,
  variableValues: Record<string, string> = {}
): string {
  const ctx: ExportContext = {
    format,
    variableValues,
    defs: [],
    defIdCounter: 0,
  };

  const container: ContainerDimensions = {
    width: format.width,
    height: format.height,
  };

  // Build background
  const bgElement = renderBackground(background, format, ctx);

  // Build layer elements (reverse for correct z-order in SVG)
  const layerElements = layers
    .filter(l => l.visible)
    .map(layer => renderLayer(layer, container, ctx))
    .join('\n');

  // Build defs section if needed
  const defsSection = ctx.defs.length > 0
    ? `<defs>\n${ctx.defs.join('\n')}\n</defs>`
    : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${format.width}" height="${format.height}" viewBox="0 0 ${format.width} ${format.height}">
${defsSection}
${bgElement}
${layerElements}
</svg>`;
}

function renderBackground(bg: CanvasBackground, format: AdFormat, ctx: ExportContext): string {
  if (bg.type === 'image') {
    return `<image href="${bg.src}" width="${format.width}" height="${format.height}" preserveAspectRatio="xMidYMid slice"/>`;
  }

  // Use brushToSvg for solid and gradient backgrounds
  const { value, def } = brushToSvg(bg as Brush, ctx, 'bg');
  if (def) {
    ctx.defs.push(def);
  }
  return `<rect width="${format.width}" height="${format.height}" fill="${value}"/>`;
}

function renderLayer(layer: Layer, container: ContainerDimensions, ctx: ExportContext): string {
  const layerWidth = typeof layer.width === 'number' ? layer.width : 100;
  const layerHeight = typeof layer.height === 'number' ? layer.height : 100;
  const layerDims = { width: layerWidth, height: layerHeight };
  const pos = resolvePosition(layer.position, container, layerDims);
  const opacity = layer.opacity !== 1 ? ` opacity="${layer.opacity}"` : '';
  const rotation = 'rotation' in layer && typeof layer.rotation === 'number' ? layer.rotation : 0;
  const transform = rotation ? ` transform="rotate(${rotation} ${pos.x + layerWidth / 2} ${pos.y + layerHeight / 2})"` : '';

  switch (layer.type) {
    case 'text':
      return renderTextLayer(layer as TextLayer, pos, opacity, transform, ctx);
    case 'shape':
      return renderShapeLayer(layer as ShapeLayer, pos, opacity, transform, ctx);
    case 'image':
      return renderImageLayer(layer as ImageLayer, pos, opacity, transform);
    case 'primitive':
      return renderPrimitiveLayer(layer as PrimitiveLayer, pos, opacity, transform);
    case 'group':
      // Recursively render group children
      const children = (layer as any).children || [];
      const childElements = children
        .filter((c: Layer) => c.visible)
        .map((c: Layer) => renderLayer(c, container, ctx))
        .join('\n');
      return `<g${opacity}${transform}>\n${childElements}\n</g>`;
    default:
      return `<!-- Unsupported layer type: ${layer.type} -->`;
  }
}

function renderTextLayer(
  layer: TextLayer,
  pos: { x: number; y: number },
  opacity: string,
  transform: string,
  ctx: ExportContext
): string {
  const { content } = layer;

  // Resolve variable bindings
  let text = content.text;
  if (content.variableBinding && ctx.variableValues[content.variableBinding]) {
    text = ctx.variableValues[content.variableBinding];
  }

  // Escape XML entities
  text = escapeXml(text);

  const fontSize = content.fontSize || 16;
  const fontFamily = content.fontFamily || 'sans-serif';
  const fontWeight = content.fontWeight || 400;
  const { value: fill, def: fillDef } = brushToSvg(content.color, ctx, 'textFill');
  if (fillDef) ctx.defs.push(fillDef);
  const textAnchor = content.textAlign === 'center' ? 'middle' : content.textAlign === 'right' ? 'end' : 'start';

  // Calculate x position based on alignment
  const layerWidth = typeof layer.width === 'number' ? layer.width : 0;
  let x = pos.x;
  if (content.textAlign === 'center' && layerWidth) {
    x = pos.x + layerWidth / 2;
  } else if (content.textAlign === 'right' && layerWidth) {
    x = pos.x + layerWidth;
  }

  // SVG text baseline is different, adjust y
  const y = pos.y + fontSize * 0.85;

  return `<text x="${x}" y="${y}" font-family="${fontFamily}" font-size="${fontSize}" font-weight="${fontWeight}" fill="${fill}" text-anchor="${textAnchor}"${opacity}${transform}>${text}</text>`;
}

function renderShapeLayer(
  layer: ShapeLayer,
  pos: { x: number; y: number },
  opacity: string,
  transform: string,
  ctx: ExportContext
): string {
  const { content } = layer;
  const width = typeof layer.width === 'number' ? layer.width : 100;
  const height = typeof layer.height === 'number' ? layer.height : 100;

  const { value: fill, def: fillDef } = brushToSvg(content.fill, ctx, 'shapeFill');
  const { value: stroke, def: strokeDef } = brushToSvg(content.stroke, ctx, 'shapeStroke');
  if (fillDef) ctx.defs.push(fillDef);
  if (strokeDef) ctx.defs.push(strokeDef);

  const strokeWidth = content.strokeWidth || 0;
  const strokeAttr = stroke !== 'none' ? ` stroke="${stroke}" stroke-width="${strokeWidth}"` : '';

  switch (content.shapeType) {
    case 'rectangle': {
      const rx = content.borderRadius || 0;
      return `<rect x="${pos.x}" y="${pos.y}" width="${width}" height="${height}" rx="${rx}" fill="${fill}"${strokeAttr}${opacity}${transform}/>`;
    }
    case 'circle': {
      const r = Math.min(width, height) / 2;
      const cx = pos.x + width / 2;
      const cy = pos.y + height / 2;
      return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}"${strokeAttr}${opacity}${transform}/>`;
    }
    case 'ellipse': {
      const rx = width / 2;
      const ry = height / 2;
      const cx = pos.x + rx;
      const cy = pos.y + ry;
      return `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="${fill}"${strokeAttr}${opacity}${transform}/>`;
    }
    case 'line': {
      const x2 = pos.x + width;
      const y2 = pos.y + height;
      const lineStroke = stroke !== 'none' ? stroke : '#000';
      return `<line x1="${pos.x}" y1="${pos.y}" x2="${x2}" y2="${y2}" stroke="${lineStroke}" stroke-width="${strokeWidth || 1}"${opacity}${transform}/>`;
    }
    default:
      return `<!-- Unsupported shape type: ${content.shapeType} -->`;
  }
}

function renderImageLayer(
  layer: ImageLayer,
  pos: { x: number; y: number },
  opacity: string,
  transform: string
): string {
  const { content } = layer;
  const width = typeof layer.width === 'number' ? layer.width : 100;
  const height = typeof layer.height === 'number' ? layer.height : 100;

  // For SVG, we embed the image
  const preserveAspectRatio = content.objectFit === 'contain' ? 'xMidYMid meet' :
                              content.objectFit === 'cover' ? 'xMidYMid slice' : 'none';

  return `<image href="${content.src}" x="${pos.x}" y="${pos.y}" width="${width}" height="${height}" preserveAspectRatio="${preserveAspectRatio}"${opacity}${transform}/>`;
}

function renderPrimitiveLayer(
  layer: PrimitiveLayer,
  pos: { x: number; y: number },
  opacity: string,
  transform: string
): string {
  // Primitives are complex custom components - we'll render a placeholder
  const width = typeof layer.width === 'number' ? layer.width : 100;
  const height = typeof layer.height === 'number' ? layer.height : 100;

  return `<!-- Primitive: ${layer.content.primitiveType} -->
<rect x="${pos.x}" y="${pos.y}" width="${width}" height="${height}" fill="none" stroke="#888" stroke-dasharray="4"${opacity}${transform}/>
<text x="${pos.x + width/2}" y="${pos.y + height/2}" text-anchor="middle" fill="#888" font-size="10">${layer.content.primitiveType}</text>`;
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Download SVG as file
 */
export function downloadSVG(svgString: string, filename: string): void {
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.svg') ? filename : `${filename}.svg`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
